import { TuneOutlined } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, CardContainer, Dropdown } from 'finara-components';
import type { DropdownOption } from 'finara-components';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';

import { LoadingState } from '@/components/common';
import { Input } from '@/components/common/Input';
import { Navbar } from '@/components/super-admin/general';
import { superAdminNavItems } from '@/data/super-admin/general/navigationData';
import { orgConfigKeys } from '@/hooks/queries/useOrganizationConfig';
import {
  organizationConfigService,
  type RatePeriod,
} from '@/services/organizationConfig.service';

const periodOptions: DropdownOption[] = [
  { value: 'daily', label: 'Diario' },
  { value: 'yearly', label: 'Anual' },
  { value: 'monthly', label: 'Mensual' },
  { value: 'weekly', label: 'Semanal' },
  { value: 'hourly', label: 'Por Hora' },
];

const frequencyOptions: DropdownOption[] = [
  { value: 'biweekly', label: 'Quincenal' },
  { value: 'monthly', label: 'Mensual' },
];

export const OrgConfigPage: React.FC = () => {
  const { organizationId = '' } = useParams<{ organizationId: string }>();
  const queryClient = useQueryClient();
  const [autoApprove, setAutoApprove] = useState(false);

  const credit = useQuery({
    queryKey: ['orgConfig', organizationId, 'credit'],
    queryFn: () => organizationConfigService.getOrgCreditSettings(organizationId),
    enabled: !!organizationId,
  });
  const limits = useQuery({
    queryKey: ['orgConfig', organizationId, 'limits'],
    queryFn: () => organizationConfigService.getOrgTransactionLimits(organizationId),
    enabled: !!organizationId,
  });
  const payroll = useQuery({
    queryKey: ['orgConfig', organizationId, 'payroll'],
    queryFn: () => organizationConfigService.getOrgPayrollConfig(organizationId),
    enabled: !!organizationId,
  });

  // Local form state seeded from fetched values
  const [creditForm, setCreditForm] = useState({
    creditInterestRate: 0,
    period: 'daily' as RatePeriod,
    creditTermDays: 0,
    creditMaxAmount: null as number | null,
    creditMaxSalaryPercentage: 0,
  });
  const [limitsForm, setLimitsForm] = useState({
    dailyTransactionLimit: null as number | null,
    monthlyTransactionLimit: null as number | null,
    transactionLimitPerOperation: null as number | null,
  });
  const [payrollForm, setPayrollForm] = useState({
    payrollFrequency: 'biweekly' as 'biweekly' | 'monthly',
    payrollDayOfMonth1: 15,
    payrollDayOfMonth2: null as number | null,
    payrollAutoDispersion: false,
    payrollDispersionTime: '',
  });

  useEffect(() => {
    if (credit.data) {
      setCreditForm({
        creditInterestRate: credit.data.creditInterestRate,
        period: credit.data.period,
        creditTermDays: credit.data.creditTermDays,
        creditMaxAmount: credit.data.creditMaxAmount,
        creditMaxSalaryPercentage: credit.data.creditMaxSalaryPercentage,
      });
    }
  }, [credit.data]);

  useEffect(() => {
    if (limits.data) {
      setLimitsForm({
        dailyTransactionLimit: limits.data.dailyTransactionLimit,
        monthlyTransactionLimit: limits.data.monthlyTransactionLimit,
        transactionLimitPerOperation: limits.data.transactionLimitPerOperation,
      });
    }
  }, [limits.data]);

  useEffect(() => {
    if (payroll.data) {
      setPayrollForm({
        payrollFrequency: payroll.data.payrollFrequency,
        payrollDayOfMonth1: payroll.data.payrollDayOfMonth1,
        payrollDayOfMonth2: payroll.data.payrollDayOfMonth2,
        payrollAutoDispersion: payroll.data.payrollAutoDispersion,
        payrollDispersionTime: payroll.data.payrollDispersionTime,
      });
    }
  }, [payroll.data]);

  const onProposed = () => {
    void queryClient.invalidateQueries({ queryKey: [...orgConfigKeys.all, 'changes'] });
    toast.success(
      autoApprove
        ? 'Cambio aplicado (auto-aprobado).'
        : 'Cambio enviado a revisión. Otro super admin debe aprobarlo.'
    );
  };

  const proposeCredit = useMutation({
    mutationFn: () =>
      organizationConfigService.proposeOrgCreditSettings(organizationId, creditForm, autoApprove),
    onSuccess: onProposed,
    onError: () => toast.error('No se pudo proponer el cambio.'),
  });
  const proposeLimits = useMutation({
    mutationFn: () =>
      organizationConfigService.proposeOrgTransactionLimits(organizationId, limitsForm, autoApprove),
    onSuccess: onProposed,
    onError: () => toast.error('No se pudo proponer el cambio.'),
  });
  const proposePayroll = useMutation({
    mutationFn: () =>
      organizationConfigService.proposeOrgPayrollConfig(organizationId, payrollForm, autoApprove),
    onSuccess: onProposed,
    onError: () => toast.error('No se pudo proponer el cambio.'),
  });

  const isLoading = credit.isLoading || limits.isLoading || payroll.isLoading;

  return (
    <div className="min-h-screen">
      <Navbar navItems={superAdminNavItems} activeItem="Clientes" />

      <div className="px-4 py-8">
        <div className="mb-6 flex items-center gap-3">
          <TuneOutlined className="text-brand-blue" />
          <div>
            <h1 className="text-2xl font-bold text-brand-white">Parámetros de la Organización</h1>
            <p className="text-xs text-muted font-mono">{organizationId}</p>
          </div>
        </div>

        <label className="mb-6 flex items-center gap-2 text-sm text-brand-white">
          <input
            type="checkbox"
            checked={autoApprove}
            onChange={e => setAutoApprove(e.target.checked)}
          />
          Auto-aprobar (aplicar inmediatamente sin segundo super admin)
        </label>

        {isLoading ? (
          <LoadingState message="Cargando parámetros..." />
        ) : (
          <div className="space-y-6">
            {/* CREDIT */}
            <CardContainer>
              <form
                className="p-5 space-y-4"
                onSubmit={e => {
                  e.preventDefault();
                  proposeCredit.mutate();
                }}
              >
                <h3 className="font-semibold text-brand-white">Créditos</h3>
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    id="ci"
                    label="Tasa de Interés (%)"
                    type="number"
                    step="0.01"
                    value={creditForm.creditInterestRate}
                    onChange={e =>
                      setCreditForm(p => ({ ...p, creditInterestRate: Number(e.target.value) }))
                    }
                  />
                  <div>
                    <label htmlFor="cp" className="block text-sm font-medium text-brand-white mb-1">Período</label>
                    <Dropdown
                      id="cp"
                      value={creditForm.period}
                      onChange={v => setCreditForm(p => ({ ...p, period: String(v) as RatePeriod }))}
                      options={periodOptions}
                    />
                  </div>
                  <Input
                    id="ct"
                    label="Plazo (días)"
                    type="number"
                    value={creditForm.creditTermDays}
                    onChange={e =>
                      setCreditForm(p => ({ ...p, creditTermDays: Number(e.target.value) }))
                    }
                  />
                  <Input
                    id="cs"
                    label="% Máx. de Salario"
                    type="number"
                    value={creditForm.creditMaxSalaryPercentage}
                    onChange={e =>
                      setCreditForm(p => ({
                        ...p,
                        creditMaxSalaryPercentage: Number(e.target.value),
                      }))
                    }
                  />
                  <Input
                    id="cm"
                    label="Monto Máx. (opcional)"
                    type="number"
                    value={creditForm.creditMaxAmount ?? ''}
                    onChange={e =>
                      setCreditForm(p => ({
                        ...p,
                        creditMaxAmount: e.target.value ? Number(e.target.value) : null,
                      }))
                    }
                  />
                </div>
                <Button type="submit" variant="black" size="small" disabled={proposeCredit.isPending}>
                  {autoApprove ? 'Aplicar' : 'Proponer cambio'}
                </Button>
              </form>
            </CardContainer>

            {/* LIMITS */}
            <CardContainer>
              <form
                className="p-5 space-y-4"
                onSubmit={e => {
                  e.preventDefault();
                  proposeLimits.mutate();
                }}
              >
                <h3 className="font-semibold text-brand-white">Límites de Transacción</h3>
                <div className="grid grid-cols-3 gap-4">
                  <Input
                    id="ld"
                    label="Diario"
                    type="number"
                    value={limitsForm.dailyTransactionLimit ?? ''}
                    onChange={e =>
                      setLimitsForm(p => ({
                        ...p,
                        dailyTransactionLimit: e.target.value ? Number(e.target.value) : null,
                      }))
                    }
                  />
                  <Input
                    id="lm"
                    label="Mensual"
                    type="number"
                    value={limitsForm.monthlyTransactionLimit ?? ''}
                    onChange={e =>
                      setLimitsForm(p => ({
                        ...p,
                        monthlyTransactionLimit: e.target.value ? Number(e.target.value) : null,
                      }))
                    }
                  />
                  <Input
                    id="lo"
                    label="Por Operación"
                    type="number"
                    value={limitsForm.transactionLimitPerOperation ?? ''}
                    onChange={e =>
                      setLimitsForm(p => ({
                        ...p,
                        transactionLimitPerOperation: e.target.value ? Number(e.target.value) : null,
                      }))
                    }
                  />
                </div>
                <Button type="submit" variant="black" size="small" disabled={proposeLimits.isPending}>
                  {autoApprove ? 'Aplicar' : 'Proponer cambio'}
                </Button>
              </form>
            </CardContainer>

            {/* PAYROLL */}
            <CardContainer>
              <form
                className="p-5 space-y-4"
                onSubmit={e => {
                  e.preventDefault();
                  proposePayroll.mutate();
                }}
              >
                <h3 className="font-semibold text-brand-white">Nómina</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="pf" className="block text-sm font-medium text-brand-white mb-1">
                      Frecuencia
                    </label>
                    <Dropdown
                      id="pf"
                      value={payrollForm.payrollFrequency}
                      onChange={v =>
                        setPayrollForm(p => ({
                          ...p,
                          payrollFrequency: String(v) as typeof p.payrollFrequency,
                        }))
                      }
                      options={frequencyOptions}
                    />
                  </div>
                  <Input
                    id="pd1"
                    label="Día de pago 1"
                    type="number"
                    value={payrollForm.payrollDayOfMonth1}
                    onChange={e =>
                      setPayrollForm(p => ({ ...p, payrollDayOfMonth1: Number(e.target.value) }))
                    }
                  />
                  <Input
                    id="pd2"
                    label="Día de pago 2 (vacío = fin de mes)"
                    type="number"
                    value={payrollForm.payrollDayOfMonth2 ?? ''}
                    onChange={e =>
                      setPayrollForm(p => ({
                        ...p,
                        payrollDayOfMonth2: e.target.value ? Number(e.target.value) : null,
                      }))
                    }
                  />
                  <Input
                    id="pt"
                    label="Hora de dispersión (HH:MM:SS)"
                    type="text"
                    value={payrollForm.payrollDispersionTime}
                    onChange={e =>
                      setPayrollForm(p => ({ ...p, payrollDispersionTime: e.target.value }))
                    }
                  />
                </div>
                <label className="flex items-center gap-2 text-sm text-brand-white">
                  <input
                    type="checkbox"
                    checked={payrollForm.payrollAutoDispersion}
                    onChange={e =>
                      setPayrollForm(p => ({ ...p, payrollAutoDispersion: e.target.checked }))
                    }
                  />
                  Dispersión automática
                </label>
                <Button type="submit" variant="black" size="small" disabled={proposePayroll.isPending}>
                  {autoApprove ? 'Aplicar' : 'Proponer cambio'}
                </Button>
              </form>
            </CardContainer>
          </div>
        )}
      </div>
    </div>
  );
};
